import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Plus, Sparkles, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { canvasAIApi } from '@/lib/api/client'
import type { CanvasSection } from './canvasTemplates'

// Suggestion from AI
interface AISuggestion {
  id: string
  label: string
  description?: string
  entityType: string
  confidence: 'high' | 'medium' | 'low'
  reasoning?: string
}

interface SectionSuggestionsPopupProps {
  section: CanvasSection
  canvasInstanceUri: string
  accountId: string
  projectId: string
  apiKey: string
  canvasContext?: Record<string, string[]>
  isOpen: boolean
  onClose: () => void
  onAddSuggestion: (suggestion: {
    type: string
    label: string
    description?: string
    sectionId: string
    confidence: number
  }) => void
  anchorRef?: React.RefObject<HTMLElement | null>
}

export function SectionSuggestionsPopup({
  section,
  canvasInstanceUri,
  accountId,
  projectId,
  apiKey,
  canvasContext,
  isOpen,
  onClose,
  onAddSuggestion,
  anchorRef,
}: SectionSuggestionsPopupProps) {
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set())
  const popupRef = useRef<HTMLDivElement>(null)

  // Fetch suggestions when popup opens
  const fetchSuggestions = useCallback(async () => {
    if (!isOpen || !apiKey) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await canvasAIApi.generateSuggestions(
        accountId,
        projectId,
        canvasInstanceUri,
        {
          sectionKey: section.id,
          apiKey,
          canvasContext,
          limit: 5,
        }
      )
      setSuggestions(result.suggestions)
    } catch (err) {
      console.error('Failed to fetch suggestions:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate suggestions')
    } finally {
      setIsLoading(false)
    }
  }, [isOpen, apiKey, accountId, projectId, canvasInstanceUri, section.id, canvasContext])

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions()
      setAddedIds(new Set())
    }
  }, [isOpen, fetchSuggestions])

  // Handle clicking outside to close
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        anchorRef?.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, anchorRef])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Handle adding a suggestion
  const handleAddSuggestion = (suggestion: AISuggestion) => {
    const confidenceMap: Record<string, number> = {
      high: 0.9,
      medium: 0.7,
      low: 0.5,
    }

    onAddSuggestion({
      type: suggestion.entityType,
      label: suggestion.label,
      description: suggestion.description,
      sectionId: section.id,
      confidence: confidenceMap[suggestion.confidence] || 0.7,
    })

    setAddedIds((prev) => new Set([...prev, suggestion.id]))
  }

  if (!isOpen) return null

  // Confidence badge colors
  const confidenceColors: Record<string, { bg: string; text: string; border: string }> = {
    high: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    medium: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    low: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
  }

  return (
    <div
      ref={popupRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '4px',
        width: '320px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: `${section.color}08`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles style={{ width: '16px', height: '16px', color: section.color }} />
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
            AI Suggestions
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {!isLoading && (
            <button
              onClick={fetchSuggestions}
              style={{
                width: '26px',
                height: '26px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Refresh suggestions"
            >
              <RefreshCw style={{ width: '14px', height: '14px', color: '#6b7280' }} />
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X style={{ width: '14px', height: '14px', color: '#6b7280' }} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
        {isLoading ? (
          <div
            style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Loader2
              style={{
                width: '24px',
                height: '24px',
                color: section.color,
                animation: 'spin 1s linear infinite',
              }}
            />
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Generating suggestions...
            </span>
          </div>
        ) : error ? (
          <div
            style={{
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center',
            }}
          >
            <AlertCircle style={{ width: '24px', height: '24px', color: '#ef4444' }} />
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{error}</span>
            <button
              onClick={fetchSuggestions}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 500,
                color: section.color,
                backgroundColor: `${section.color}10`,
                border: `1px solid ${section.color}30`,
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        ) : suggestions.length === 0 ? (
          <div
            style={{
              padding: '32px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '12px',
            }}
          >
            No suggestions available. Try adding more content to other sections first.
          </div>
        ) : (
          <div style={{ padding: '8px' }}>
            {suggestions.map((suggestion) => {
              const isAdded = addedIds.has(suggestion.id)
              const colors = confidenceColors[suggestion.confidence] || confidenceColors.medium

              return (
                <div
                  key={suggestion.id}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    backgroundColor: isAdded ? '#f9fafb' : 'transparent',
                    marginBottom: '4px',
                    transition: 'background-color 0.15s',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '6px',
                          marginBottom: '4px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 500,
                            color: isAdded ? '#9ca3af' : '#111827',
                            textDecoration: isAdded ? 'line-through' : 'none',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {suggestion.label}
                        </span>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 500,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: colors.bg,
                            color: colors.text,
                            border: `1px solid ${colors.border}`,
                            textTransform: 'capitalize',
                          }}
                        >
                          {suggestion.confidence}
                        </span>
                      </div>
                      {suggestion.description && (
                        <p
                          style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            lineHeight: 1.4,
                            margin: 0,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {suggestion.description}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddSuggestion(suggestion)}
                      disabled={isAdded}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        border: `1px solid ${isAdded ? '#e5e7eb' : section.color + '40'}`,
                        backgroundColor: isAdded ? '#f3f4f6' : 'white',
                        cursor: isAdded ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        opacity: isAdded ? 0.5 : 1,
                        transition: 'all 0.15s',
                      }}
                      title={isAdded ? 'Added' : 'Add to canvas'}
                    >
                      <Plus
                        style={{
                          width: '14px',
                          height: '14px',
                          color: isAdded ? '#9ca3af' : section.color,
                        }}
                      />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer - Section context */}
      <div
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
        }}
      >
        <p style={{ fontSize: '10px', color: '#9ca3af', margin: 0 }}>
          Suggestions for: {section.title}
        </p>
      </div>
    </div>
  )
}
